import type {ExportInvoiceModal_Viewer$data as ExportInvoiceModal_Viewer} from './__generated__/ExportInvoiceModal_Viewer.graphql';
import {IntlShape} from 'react-intl';

export interface ExportInvoiceModalProps {
	closeModal: () => void;
	viewer: ExportInvoiceModal_Viewer;
	billToId: string;
}

export type Invoice = NonNullable<ExportInvoiceModal_Viewer['invoice']>;

export type InvoiceEntry = NonNullable<
	NonNullable<NonNullable<NonNullable<NonNullable<ExportInvoiceModal_Viewer['invoice']>['entries']>['edges']>[0]>['node']
>;

export type InvoiceBillTo = NonNullable<ExportInvoiceModal_Viewer['billTo']>;

export type XeroAccountItem = {
	value: string;
	label: string;
	taxRate: number;
	type: string;
	code: string;
};

export type InvoiceEntryRow = {
	projectColor: string;
	projectId: string;
	customProjectId: string | null | undefined;
	entryDescription: string | null;
	totalPrice?: number;
	discountPercentage?: number;
	taxPercentage?: number;
	label?: string;
	xeroAccount?: string;
	accountCode?: string;
	quickbooksItem?: string;
	quickbooksTaxable?: boolean;
	quickbooksUnitPrice?: number;
	economicProduct?: string;
	/*
	 * The Xero line selections, added by T4-132. All optional and unset on the flag-off path,
	 * so no existing consumer of this type sees a change.
	 */
	xeroItemId?: string;
	xeroItemCode?: string;
	xeroTaxRate?: number;
	xeroTaxType?: string;
	xeroTracking?: Record<string, string>;
} & InvoiceEntry;

export type ExportInvoiceOnSuccess = () => void;

export type OnErrorType = (error: Error, onFailure: (error: Error) => void) => void;

export interface ExportInvoiceParams {
	onSuccess: ExportInvoiceOnSuccess;
	intl: IntlShape;
	invoice: Invoice;
	invoiceRows: InvoiceEntryRow[];
	billTo: NonNullable<InvoiceBillTo>;
	onError: OnErrorType;
}

export interface ExportXeroInvoiceParams extends ExportInvoiceParams {
	projectInDescription: boolean;
	/*
	 * The gate resolved ONCE per render by the modal, passed in rather than re-evaluated here.
	 * Two independent reads of the flag store in one export can disagree if it changes
	 * mid-session, and then the payload shape does not match the table the user was looking at.
	 */
	useLinesTable: boolean;
}

export interface ExportQuickbooksInvoiceParams extends ExportInvoiceParams {
	quickbookSettings: QuickbookSetting[];
	taxCode: TaxCode | null;
}

export interface TaxCode {
	value: string;
	label: string;
	taxRate: number;
}

export interface QuickbookProductService {
	value: string;
	label: string;
	description: string;
	unitPrice: number;
	taxable: boolean;
}

export interface EconomicProduct {
	value: string;
	label: string;
	description: string;
	unitPrice: number;
	taxable: boolean;
}

export enum QuickbooksSettingId {
	CONTROL_INVOICE_REFERENCE,
	USE_PRODUCT_SERVICE_DESCRIPTIONS,
	INCLUDE_PROJECT_NAME_IN_DESCRIPTION,
	USE_QUICKBOOKS_TERMS,
	USE_CUSTOMER_DEFAULT_TAX,
}

export interface QuickbookSetting {
	id: QuickbooksSettingId;
	name: string;
	checked: boolean;
	disabled: boolean;
}

export interface QuickbookSettingsMutationInput {
	letQBOSetDescription?: boolean;
	letQBOSetInvoiceRef?: boolean;
	useQBOTerms?: boolean;
	useQBODefaultTax?: boolean;
	includeProjectNameInDesc?: boolean;
}
